import {
  BobcatTagDTO,
  useCreateGroupTicketProductHook,
  useGetUserAllowedOrganizationTags,
  useLookupBobcatOffersByTags,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { FormSection } from '@/components/FormSection';
import Button from '@/components/core/Button';
import { Spinner } from '@/components/core/Spinner';
import { StaticTickets } from './StaticTickets';
import { DynamicTickets } from './DynamicTickets';
import { TravellerClassSelector } from './TravellerClassSelector';
import { useGetCorporateStaticOffers } from '@/hooks/useGetCorporateStaticOffers';
import type { BaseTicket, ProductSet } from '@/types/buyTicket';
import {
  PRODUCT_CLASS_PREFIX,
  TRAVELLER_CLASS_ADULT,
  TRAVELLER_CLASS_PREFIX,
} from '@/helpers/ticketClass';

export const TicketSelection = ({
  setSelectedProductSet,
  staticTicket,
  setStaticTicket,
}: {
  selectedProductSet: ProductSet | undefined;
  setSelectedProductSet: (productSet: ProductSet | undefined) => void;
  staticTicket: boolean;
  setStaticTicket: (staticTicket: boolean) => void;
}) => {
  const { formatMessage } = useIntl();

  const [baseTicket, setBaseTicket] = useState<BaseTicket | undefined>(
    undefined,
  );
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const { data: tags } = useGetUserAllowedOrganizationTags();
  const { allowedOffers } = useGetCorporateStaticOffers();

  const { allowedTravellerClasses, allowedProductClasses, tagTitles } =
    useMemo(() => {
      const allowedTravellerClasses: BobcatTagDTO[] = [];
      const allowedProductClasses: BobcatTagDTO[] = [];
      const tagTitles: Record<string, string> = {};
      if (!tags) {
        return { allowedTravellerClasses, allowedProductClasses, tagTitles };
      }

      tags.forEach((tag) => {
        tagTitles[tag.tag_id] = tag.title;
        if (tag.collection_type === 'master') {
          if (tag.tag_id.startsWith(TRAVELLER_CLASS_PREFIX)) {
            allowedTravellerClasses.push(tag);
          } else if (tag.tag_id.startsWith(PRODUCT_CLASS_PREFIX)) {
            allowedProductClasses.push(tag);
          }
        }
      });
      return { allowedTravellerClasses, allowedProductClasses, tagTitles };
    }, [tags]);

  const allowedTravellerClassIds = useMemo(
    () => new Set(allowedTravellerClasses.map((tag) => tag.tag_id)),
    [allowedTravellerClasses],
  );

  const availableTravellerClasses = useMemo(
    () =>
      baseTicket?.availableTravellerClasses.filter((tagId) =>
        allowedTravellerClassIds.has(tagId),
      ) ?? [],
    [baseTicket, allowedTravellerClassIds],
  );

  const showTicketTypeSelection = useMemo(() => {
    if (!allowedOffers || !tags) {
      return false;
    }
    return (
      allowedTravellerClasses.length > 0 &&
      allowedProductClasses.length > 0 &&
      allowedOffers.length > 0
    );
  }, [
    allowedOffers,
    tags,
    allowedTravellerClasses.length,
    allowedProductClasses.length,
  ]);

  useEffect(() => {
    if (!showTicketTypeSelection) {
      setStaticTicket(allowedOffers.length > 0);
    }
  }, [showTicketTypeSelection, allowedOffers.length, setStaticTicket]);

  const baseTicketId = baseTicket?.product_set_id;

  // Reconcile traveller-class quantities when the selected base ticket changes.
  // A groupable ticket keeps every still-supported class (and its quantity) so
  // switching tickets preserves the group selection; classes the new ticket
  // doesn't support are dropped. A non-groupable ticket keeps a single supported
  // class. Keyed on the id so background refetches keep quantities, and the
  // selection survives the brief undefined state when toggling between static
  // and dynamic tickets.
  useEffect(() => {
    if (!baseTicket) {
      return;
    }
    if (availableTravellerClasses.length === 0) {
      setQuantities({});
      return;
    }
    setQuantities((current) => {
      const compatible = Object.keys(current)
        .filter(
          (tagId) =>
            current[tagId] > 0 && availableTravellerClasses.includes(tagId),
        )
        .sort();

      if (compatible.length === 0) {
        const defaultClass = availableTravellerClasses.includes(
          TRAVELLER_CLASS_ADULT,
        )
          ? TRAVELLER_CLASS_ADULT
          : availableTravellerClasses[0];
        return { [defaultClass]: 1 };
      }

      if (!baseTicket.allowsMultipleTravellerClasses) {
        const single = compatible.includes(TRAVELLER_CLASS_ADULT)
          ? TRAVELLER_CLASS_ADULT
          : compatible[0];
        return { [single]: 1 };
      }

      return Object.fromEntries(
        compatible.map((tagId) => [tagId, current[tagId]]),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTicketId]);

  const tcTags = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, quantity]) => quantity > 0)
        .map(([tagId, quantity]) => `${tagId}*${quantity}`)
        .sort(),
    [quantities],
  );

  const activeTravellerClasses = useMemo(
    () => Object.entries(quantities).filter(([, quantity]) => quantity > 0),
    [quantities],
  );

  // A single traveller (one class, quantity one) reuses the existing offer /
  // journey product set rather than creating a group ticket.
  const isSingleTraveller =
    activeTravellerClasses.length === 1 && activeTravellerClasses[0][1] === 1;
  const singleClassTag = isSingleTraveller
    ? activeTravellerClasses[0][0]
    : undefined;
  const singleVariant =
    singleClassTag && baseTicket
      ? baseTicket.variants[singleClassTag]
      : undefined;

  const hasSelection = !!baseTicket && tcTags.length > 0;
  const useGroupEndpoint =
    hasSelection &&
    !isSingleTraveller &&
    !!baseTicket?.allowsMultipleTravellerClasses;
  // Static offers never expose a directly purchasable product set id, so every
  // static selection resolves the concrete product set through the tag lookup
  // below — not only the single-traveller variant.
  const useStaticLookup = hasSelection && staticTicket;
  const useStaticSingle =
    useStaticLookup && isSingleTraveller && !!singleVariant;
  const useDirectVariant =
    hasSelection && isSingleTraveller && !staticTicket && !!singleVariant;

  // A single traveller resolves its specific variant; a group resolves the base
  // ticket whose product set seeds the group endpoint. Dynamic offers already
  // carry purchasable ids and skip the lookup entirely.
  const lookupTags = useMemo(() => {
    if (!useStaticLookup || !baseTicket) {
      return '';
    }
    const sourceTags =
      isSingleTraveller && singleVariant ? singleVariant.tags : baseTicket.tags;
    return sourceTags
      .filter(
        (tag) =>
          tag.startsWith(TRAVELLER_CLASS_PREFIX) ||
          tag.startsWith(PRODUCT_CLASS_PREFIX) ||
          tag.startsWith('pds_'),
      )
      .join(',');
  }, [useStaticLookup, baseTicket, isSingleTraveller, singleVariant]);

  const {
    data: shallowOffers,
    isFetching: isLookupFetching,
    isError: isLookupError,
  } = useLookupBobcatOffersByTags(
    { tags: lookupTags },
    { query: { enabled: useStaticLookup && lookupTags.length > 0 } },
  );

  const resolvedStaticProductSetId =
    useStaticLookup && shallowOffers && shallowOffers.length > 0
      ? shallowOffers[0].product_set_id
      : undefined;

  // The group endpoint seeds from the resolved static product set when buying a
  // static offer, and from the journey product set otherwise.
  const groupBaseProductSetId = staticTicket
    ? resolvedStaticProductSetId
    : baseTicketId;

  const [debouncedTcTags] = useDebounce(tcTags, 400);
  const isDebouncePending = tcTags.join(',') !== debouncedTcTags.join(',');

  // After switching tickets the carried-over selection may still include classes
  // the new ticket doesn't support (until the effect above trims them). Guard the
  // group request so it never prices a stale class for the new ticket, which
  // would otherwise fire an extra createGroupTicket call.
  const debouncedTagsValid =
    !!baseTicket &&
    debouncedTcTags.every((tag) =>
      availableTravellerClasses.includes(tag.split('*')[0]),
    );

  const createGroupTicket = useCreateGroupTicketProductHook();

  const {
    data: groupTicketData,
    isFetching: isGroupFetching,
    isError: isGroupError,
  } = useQuery({
    queryKey: ['group-ticket-price', groupBaseProductSetId, debouncedTcTags],
    queryFn: ({ signal }) =>
      createGroupTicket(
        {
          product_set_id: groupBaseProductSetId as string,
          tc_tags: debouncedTcTags,
        },
        signal,
      ),
    enabled:
      useGroupEndpoint &&
      !!groupBaseProductSetId &&
      debouncedTcTags.length > 0 &&
      !isDebouncePending &&
      debouncedTagsValid,
  });

  const resolvedProductSet = useMemo<ProductSet | undefined>(() => {
    if (!baseTicket) {
      return undefined;
    }

    if (useDirectVariant && singleVariant) {
      return {
        product_set_id: singleVariant.product_set_id,
        title: baseTicket.title,
        amount: singleVariant.amount,
        currency: baseTicket.currency,
      };
    }

    if (useStaticSingle && singleVariant) {
      if (isLookupFetching || isLookupError || !resolvedStaticProductSetId) {
        return undefined;
      }
      return {
        product_set_id: resolvedStaticProductSetId,
        title: baseTicket.title,
        amount: singleVariant.amount,
        currency: baseTicket.currency,
      };
    }

    if (useGroupEndpoint) {
      if (
        isDebouncePending ||
        isGroupFetching ||
        isGroupError ||
        !groupTicketData
      ) {
        return undefined;
      }
      return {
        product_set_id: groupTicketData.product_set_id,
        title: baseTicket.title,
        amount: groupTicketData.amount_inc_vat,
        currency: baseTicket.currency,
      };
    }

    return undefined;
  }, [
    baseTicket,
    useDirectVariant,
    useStaticSingle,
    useGroupEndpoint,
    singleVariant,
    resolvedStaticProductSetId,
    isLookupFetching,
    isLookupError,
    isDebouncePending,
    isGroupFetching,
    isGroupError,
    groupTicketData,
  ]);

  useEffect(() => {
    setSelectedProductSet(resolvedProductSet);
  }, [resolvedProductSet, setSelectedProductSet]);

  const displayPrice = useMemo<
    { amount: number; currency: string } | undefined
  >(() => {
    if (!baseTicket) {
      return undefined;
    }
    if ((useDirectVariant || useStaticSingle) && singleVariant) {
      return { amount: singleVariant.amount, currency: baseTicket.currency };
    }
    if (useGroupEndpoint && groupTicketData) {
      return {
        amount: groupTicketData.amount_inc_vat,
        currency: baseTicket.currency,
      };
    }
    return undefined;
  }, [
    baseTicket,
    useDirectVariant,
    useStaticSingle,
    useGroupEndpoint,
    singleVariant,
    groupTicketData,
  ]);

  const isResolvingPrice =
    useGroupEndpoint &&
    (isDebouncePending ||
      isGroupFetching ||
      (staticTicket && isLookupFetching));
  const isPriceError =
    (useStaticSingle && isLookupError) ||
    (useGroupEndpoint && (isGroupError || (staticTicket && isLookupError)));

  const handleSelectTicketType = useCallback(
    (isStatic: boolean) => {
      setStaticTicket(isStatic);
      setBaseTicket(undefined);
    },
    [setStaticTicket],
  );

  return (
    <FormSection title={formatMessage({ defaultMessage: 'Välj biljett' })}>
      {showTicketTypeSelection ? (
        <>
          <h3>
            <FormattedMessage defaultMessage="Biljettyp" />
          </h3>
          <div className="mb-4 flex flex-wrap gap-2">
            <Button
              variant={!staticTicket ? 'filled' : 'ghost'}
              onClick={() => handleSelectTicketType(false)}
            >
              {formatMessage({ defaultMessage: 'Resesök' })}
            </Button>
            <Button
              variant={staticTicket ? 'filled' : 'ghost'}
              onClick={() => handleSelectTicketType(true)}
            >
              {formatMessage({ defaultMessage: 'Statisk biljett' })}
            </Button>
          </div>
        </>
      ) : null}

      {staticTicket ? (
        <StaticTickets
          selectedBaseTicketId={baseTicketId}
          onSelectBaseTicket={setBaseTicket}
        />
      ) : (
        <DynamicTickets
          selectedBaseTicketId={baseTicketId}
          onSelectBaseTicket={setBaseTicket}
        />
      )}

      {baseTicket && availableTravellerClasses.length > 0 && (
        <div className="mt-4 flex flex-col gap-3">
          <TravellerClassSelector
            availableTravellerClasses={availableTravellerClasses}
            tagTitles={tagTitles}
            quantities={quantities}
            allowMultiple={baseTicket.allowsMultipleTravellerClasses}
            onChange={setQuantities}
          />
          {isPriceError ? (
            <p className="rounded-md border border-red-700 bg-red-100 p-4 text-red-700">
              <FormattedMessage defaultMessage="Kunde inte beräkna pris för valda resenärer." />
            </p>
          ) : (
            <div className="flex items-center justify-between border-t border-background-300 pt-3">
              <span className="font-medium text-text-900">
                <FormattedMessage defaultMessage="Pris" />
              </span>
              {isResolvingPrice ? (
                <Spinner className="size-5" />
              ) : displayPrice ? (
                <span className="text-lg font-semibold text-text-950">
                  {`${Math.round(displayPrice.amount * 100) / 100} ${displayPrice.currency}`}
                </span>
              ) : (
                <span className="text-text-700">—</span>
              )}
            </div>
          )}
        </div>
      )}
    </FormSection>
  );
};
